import { Controller } from "@hotwired/stimulus"

// Core::MultiSelectComponent — a searchable, multi-select dropdown.
//
// The trigger lives in the controller element; the dropdown panel is
// portalled to <body> on open so it escapes the board's overflow clipping
// and positions itself under the trigger (flipping above when there's no
// room below). Because the panel is no longer a descendant of the
// controller element while open, Stimulus target getters can't see it — so
// the panel's elements are cached in `connect()` and its search / option /
// keyboard listeners are attached manually rather than through
// `data-action`.
//
// Selection toggles per click and the panel stays open so several values
// can be picked; each selected value is mirrored to a hidden
// `<input name="…[]">` so the enclosing form submits the whole set. The
// form is submitted once, on close, only when the selection changed — so
// typing and picking never reload the page mid-interaction.
export default class extends Controller {
  static targets = ["trigger", "summary", "dropdown", "inputs"]
  static values = {
    inputName: String,
    placeholder: String,
    countNoun: { type: String, default: "selected" }
  }

  connect() {
    // Cache the portalled panel and its inner elements while they are still
    // in the controller's subtree — after portalling the Stimulus getters
    // can no longer resolve them.
    this.panel = this.hasDropdownTarget ? this.dropdownTarget : null
    this.searchEl = this.panel?.querySelector(".multi-select__search")
    this.optionsEl = this.panel?.querySelector(".multi-select__options")
    this.hintEl = this.panel?.querySelector(".multi-select__hint")

    this.selected = new Set(this.initialSelection())
    this.open = false
    this.changed = false
    this.highlightIndex = -1

    this.onDocumentPointer = this.closeOnOutside.bind(this)
    this.onDocumentFocus = this.closeOnOutside.bind(this)
    this.onSearchInput = () => this.filter()
    this.onListKeydown = (event) => this.handleListKeydown(event)
    this.onOptionClick = (event) => this.handleOptionClick(event)
  }

  disconnect() {
    // Never leave the portalled panel orphaned on <body> if the controller
    // is torn down while open (Turbo navigation, morph).
    this.teardownPanel()
    this.detachGlobalListeners()
  }

  toggle(event) {
    event.preventDefault()
    if (this.open) {
      this.closePanel({ submit: true })
    } else {
      this.openPanel()
    }
  }

  onTriggerKeydown(event) {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault()
      if (!this.open) this.openPanel()
      this.moveHighlight(event.key === "ArrowUp" ? -1 : 1)
    }
  }

  // --- Open / close -------------------------------------------------

  openPanel() {
    if (this.open || !this.panel) return
    this.changed = false

    // Portal to <body>, reveal, position, then wire the panel's own
    // listeners (it is no longer in the controller's Stimulus scope).
    document.body.appendChild(this.panel)
    this.panel.hidden = false
    this.panel.classList.remove("is-hidden")
    this.position()

    this.searchEl?.addEventListener("input", this.onSearchInput)
    this.searchEl?.addEventListener("keydown", this.onListKeydown)
    this.panel.addEventListener("click", this.onOptionClick)
    document.addEventListener("pointerdown", this.onDocumentPointer, true)
    document.addEventListener("focusin", this.onDocumentFocus, true)

    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.open = true
    if (this.searchEl) this.searchEl.value = ""
    this.filter()
    this.searchEl?.focus()
  }

  closePanel({ submit = false } = {}) {
    if (!this.open) return
    this.teardownPanel()
    this.detachGlobalListeners()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.open = false

    if (submit && this.changed) {
      this.changed = false
      this.element.closest("form")?.requestSubmit()
    }
  }

  // Return the panel to the controller element (so Stimulus disconnect
  // cleans it up) and hide it. Safe to call when already closed.
  teardownPanel() {
    if (!this.panel) return
    this.searchEl?.removeEventListener("input", this.onSearchInput)
    this.searchEl?.removeEventListener("keydown", this.onListKeydown)
    this.panel.removeEventListener("click", this.onOptionClick)
    this.panel.hidden = true
    this.panel.classList.add("is-hidden")
    for (const prop of ["min-width", "left", "top", "position"]) {
      this.panel.style.removeProperty(prop)
    }
    this.element.appendChild(this.panel)
  }

  detachGlobalListeners() {
    document.removeEventListener("pointerdown", this.onDocumentPointer, true)
    document.removeEventListener("focusin", this.onDocumentFocus, true)
  }

  closeOnOutside(event) {
    if (!this.open) return
    const target = event.target
    if (this.element.contains(target) || this.panel.contains(target)) return
    this.closePanel({ submit: true })
  }

  // Fixed-position the panel under the trigger, min-width matched to it,
  // flipping above when there isn't room below.
  position() {
    const rect = this.triggerTarget.getBoundingClientRect()
    const panel = this.panel
    panel.style.position = "fixed"
    panel.style.minWidth = `${rect.width}px`
    panel.style.left = `${rect.left}px`

    const belowRoom = window.innerHeight - rect.bottom
    const panelHeight = panel.offsetHeight
    if (belowRoom < panelHeight && rect.top > belowRoom) {
      panel.style.top = `${Math.max(0, rect.top - panelHeight - 4)}px`
    } else {
      panel.style.top = `${rect.bottom + 4}px`
    }
  }

  // --- Search -------------------------------------------------------

  filter() {
    const query = this.searchEl ? this.searchEl.value.trim().toLowerCase() : ""
    let visible = 0
    for (const option of this.optionElements()) {
      const match = this.labelFor(option).includes(query)
      option.classList.toggle("is-filtered", !match)
      if (match) visible += 1
    }
    if (this.hintEl) {
      this.hintEl.hidden = visible !== 0
      this.hintEl.classList.toggle("is-hidden", visible !== 0)
    }
    this.setHighlight(-1)
  }

  // --- Keyboard -----------------------------------------------------

  handleListKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveHighlight(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveHighlight(-1)
        break
      case "Enter": {
        event.preventDefault()
        const current = this.visibleOptions()[this.highlightIndex]
        if (current) this.toggleOption(current)
        break
      }
      case "Escape":
        event.preventDefault()
        this.closePanel({ submit: true })
        this.triggerTarget.focus()
        break
      case "Tab":
        // Let focus flow naturally out of the panel; just close it.
        this.closePanel({ submit: true })
        break
    }
  }

  moveHighlight(delta) {
    const options = this.visibleOptions()
    if (options.length === 0) return
    let index = this.highlightIndex + delta
    if (index < 0) index = options.length - 1
    if (index >= options.length) index = 0
    this.setHighlight(index)
  }

  setHighlight(index) {
    this.highlightIndex = index
    const options = this.visibleOptions()
    this.optionElements().forEach((option) =>
      option.classList.remove("is-highlighted")
    )
    const current = options[index]
    if (current) {
      current.classList.add("is-highlighted")
      current.scrollIntoView({ block: "nearest" })
    }
  }

  // --- Selection ----------------------------------------------------

  handleOptionClick(event) {
    const option = event.target.closest("[data-value]")
    if (option && this.panel.contains(option)) {
      this.toggleOption(option)
    }
  }

  toggleOption(option) {
    const value = option.dataset.value
    if (this.selected.has(value)) {
      this.selected.delete(value)
      option.classList.remove("is-selected")
      option.setAttribute("aria-selected", "false")
    } else {
      this.selected.add(value)
      option.classList.add("is-selected")
      option.setAttribute("aria-selected", "true")
    }
    this.changed = true
    this.renderInputs()
    this.renderSummary()
  }

  renderInputs() {
    if (!this.hasInputsTarget) return
    this.inputsTarget.replaceChildren(
      ...[...this.selected].map((value) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.inputNameValue
        input.value = value
        return input
      })
    )
  }

  renderSummary() {
    if (!this.hasSummaryTarget) return
    const values = [...this.selected]
    let text
    if (values.length === 0) {
      text = this.placeholderValue
    } else if (values.length === 1) {
      text = this.labelTextFor(values[0]) || this.placeholderValue
    } else {
      text = `${values.length} ${this.countNounValue}`
    }
    this.summaryTarget.textContent = text
  }

  // --- Helpers ------------------------------------------------------

  initialSelection() {
    return this.optionElements()
      .filter((option) => option.classList.contains("is-selected"))
      .map((option) => option.dataset.value)
  }

  optionElements() {
    if (!this.optionsEl) return []
    return Array.from(this.optionsEl.querySelectorAll("[data-value]"))
  }

  visibleOptions() {
    return this.optionElements().filter(
      (option) => !option.classList.contains("is-filtered")
    )
  }

  labelFor(option) {
    return (option.textContent || "").trim().toLowerCase()
  }

  labelTextFor(value) {
    const option = this.optionElements().find(
      (element) => element.dataset.value === value
    )
    return option ? option.textContent.trim() : null
  }
}
